import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AiOutlineLogout } from "react-icons/ai";
import { FaChevronDown } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../context";
import { getNavigationLinks } from "./links";
import styles from "./styles.module.scss";
import {
  findActiveParentId,
  getInitials,
  getPrimaryRoleLabel,
  hasPermission,
  isLeafItemActive,
  isNavigationItemActive,
} from "./util";

type AdminLinkProps = {
  onNavigate?: () => void;
  page?: string;
  roles?: any;
  user?: any;
};

const submenuTransition = {
  duration: 0.22,
  ease: [0.32, 0.72, 0, 1],
};

const classNames = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const AdminLink = ({
  page,
  roles = "Admin",
  user,
  onNavigate,
}: AdminLinkProps) => {
  const location = useLocation();
  const { logout } = React.useContext(AuthContext);

  const availableLinks = React.useMemo(
    () =>
      getNavigationLinks(page)
        .filter((item) => hasPermission(item.allowed, roles))
        .map((item) => {
          if (!item.children?.length) return item;

          return {
            ...item,
            children: item.children.filter(
              (child) => !child.allowed || hasPermission(child.allowed, roles),
            ),
          };
        })
        .filter((item) => !item.children || item.children.length > 0),
    [page, roles],
  );

  const activeParentId = React.useMemo(
    () => findActiveParentId(availableLinks, location.pathname),
    [availableLinks, location.pathname],
  );

  const [openSection, setOpenSection] = React.useState<string | null>(
    activeParentId,
  );

  React.useEffect(() => {
    setOpenSection(activeParentId);
  }, [activeParentId]);

  const handleToggle = (id: string) => {
    setOpenSection((current) => (current === id ? null : id));
  };

  const handleNavigation = () => {
    onNavigate?.();
  };

  const handleLogout = () => {
    onNavigate?.();
    logout();
  };

  const userName = user?.Name ?? user?.fullname ?? "Authenticated User";
  const userEmail = user?.Email ?? user?.email ?? "";
  const roleLabel = getPrimaryRoleLabel(roles);

  return (
    <div className={styles.navContent}>
      <nav className={styles.links} aria-label="Primary Navigation">
        <ul className={styles.menuList}>
          {availableLinks.map((item) => {
            const active = isNavigationItemActive(location.pathname, item);

            if (item.children?.length) {
              const expanded = openSection === item.id;

              return (
                <li key={item.id} className={styles.menuGroup}>
                  <button
                    type="button"
                    className={classNames(
                      styles.menuButton,
                      active && styles.active,
                      expanded && styles.expanded,
                    )}
                    onClick={() => handleToggle(item.id)}
                    aria-expanded={expanded}
                    aria-controls={`submenu-${item.id}`}
                  >
                    <span className={styles.linkContent}>
                      <item.Icon className={styles.linkIcon} />
                      <span className={styles.linkLabel}>{item.name}</span>
                    </span>
                    <FaChevronDown
                      className={classNames(
                        styles.caret,
                        expanded && styles.caretOpen,
                      )}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {expanded ? (
                      <motion.div
                        id={`submenu-${item.id}`}
                        className={styles.submenuWrapper}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={submenuTransition}
                      >
                        <ul className={styles.submenuList}>
                          {item.children.map((child) => {
                            const childActive = isLeafItemActive(
                              location.pathname,
                              child,
                            );

                            return (
                              <li key={child.id}>
                                <Link
                                  to={child.route}
                                  className={classNames(
                                    styles.submenuLink,
                                    childActive && styles.submenuActive,
                                  )}
                                  aria-current={
                                    childActive ? "page" : undefined
                                  }
                                  onClick={handleNavigation}
                                >
                                  <child.Icon className={styles.submenuIcon} />
                                  <span>{child.name}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </li>
              );
            }

            return (
              <li key={item.id}>
                <Link
                  to={item.route ?? "#"}
                  className={classNames(
                    styles.menuLink,
                    active && styles.active,
                  )}
                  aria-current={active ? "page" : undefined}
                  onClick={handleNavigation}
                >
                  <span className={styles.linkContent}>
                    <item.Icon className={styles.linkIcon} />
                    <span className={styles.linkLabel}>{item.name}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={styles.footerCard}>
        <div className={styles.footerIdentity}>
          <div className={styles.avatar}>{getInitials(userName)}</div>
          <div className={styles.footerMeta}>
            <p className={styles.footerName}>{userName}</p>
            <span className={styles.footerRole}>{roleLabel}</span>
          </div>
        </div>

        {userEmail ? <p className={styles.footerEmail}>{userEmail}</p> : null}

        <button
          type="button"
          className={styles.logoutButton}
          onClick={handleLogout}
        >
          <AiOutlineLogout />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default AdminLink;
